import { SBox, SCol, SRow } from "@avalara/skylab-sdk/react";
import React, { useContext, useEffect, useState } from "react";
import { ERROR_PAGE_TYPES } from "../../shared/constants";
import { NavigationVisibilityContext } from "../navigation/NavigationVisibilityProvider";
import { getAssetURL } from "../../shared/Utils";
import "./assets/error.css";
import { CustomerVendorErrorState, ErrorPageProps } from "./types";

const ErrorPage = React.memo(({ type, traceId }: ErrorPageProps) => {
    const [notFound, hideNotFound] = useState<boolean>(true);
    const [systemError, hideSystemError] = useState<boolean>(true);
    const [defaultError, hideDefaultError] = useState<boolean>(true);
    const [backgroundImage, setBackgroundImage] = useState(getAssetURL("500.svg"));
    const [noCustomerVendor, hideNoCustomerVendor] = useState<boolean>(true);
    const [customerOrVendor, setCustomerOrVendor] =
        useState<CustomerVendorErrorState["entityType"]>("customer");
    const boxIconPath = getAssetURL("Box.svg");
    const computerIconPath = getAssetURL("Computer.svg");
    const shopIconPath = getAssetURL("Shop.svg");
    const [iconImagePath, setIconImagePath] = useState(computerIconPath);

    const { setHideHeader } = useContext(NavigationVisibilityContext);

    useEffect(() => {
        if (setHideHeader) (setHideHeader as (hide: boolean) => void)(true);
        return () => {
            if (setHideHeader) (setHideHeader as (hide: boolean) => void)(false);
        };
    }, [setHideHeader]);

    useEffect(() => {
        switch (type) {
            case ERROR_PAGE_TYPES.BAD_REQUEST:
                setBackgroundImage(getAssetURL("400.svg"));
                if (window.location.pathname.startsWith("/customer")) {
                    hideNoCustomerVendor(false);
                    setIconImagePath(shopIconPath);
                } else if (window.location.pathname.startsWith("/vendor")) {
                    hideNoCustomerVendor(false);
                    setCustomerOrVendor("vendor");
                    setIconImagePath(shopIconPath);
                } else {
                    hideDefaultError(false);
                }
                break;
            case ERROR_PAGE_TYPES.PAGE_NOT_FOUND:
                setBackgroundImage(getAssetURL("400.svg"));
                hideNotFound(false);
                setIconImagePath(boxIconPath);
                break;
            case ERROR_PAGE_TYPES.INTERNAL_SERVER_ERROR:
                hideSystemError(false);
                break;
            default:
                hideDefaultError(false);
                break;
        }
    }, [type]);

    const support = (
        <span>
            <p>
                If problem persists, contact
                <a className="pad-left-xs" href="https://www.avalara.com/us/en/about/support.html">
                    Avalara support
                </a>
            </p>
        </span>
    );

    const searchRedirect = (): void => {
        window.location.href = `/search/${customerOrVendor}s`;
    };

    return (
        <div
            className={`flex dl-flex-fill-height dl-scroll dl-background`}
            style={{
                backgroundImage: `url(${backgroundImage})`,
            }}>
            <SRow>
                <SCol className="margin-top-xl text-center">
                    <SBox>
                        <div className="pad-all-lg" id="errorMessage">
                            <span>
                                <img src={iconImagePath} alt="Not Found" className="pad-right-lg" />
                            </span>
                            <span hidden={notFound} className="float-right pad-top-lg">
                                <h1 className="margin-all-none font-bold">
                                    We can&apos;t find that page
                                </h1>
                                <p className="text-md margin-top-lg">
                                    The page may have been moved, renamed, or deleted.
                                    <br />
                                </p>
                                <span>
                                    <a className="pad-right-xs" href="https://www.avalara.com">
                                        Go to the homepage
                                    </a>
                                    or use the navigation menu to find <br /> where you want to go
                                </span>
                            </span>
                            <span hidden={noCustomerVendor} className="float-right pad-top-lg">
                                <h1 className="margin-all-none font-bold">
                                    We can&apos;t find that {customerOrVendor}
                                </h1>
                                <p className="text-md margin-top-lg">
                                    Let&apos;s find what you&apos;re looking for.
                                    <br />
                                </p>
                                <button
                                    onClick={searchRedirect}
                                    type="button"
                                    className="primary inline">
                                    Search for a {customerOrVendor}
                                </button>
                            </span>
                            <div hidden={systemError} className="float-right pad-top-lg">
                                <span>
                                    <h1 className="margin-all-none font-bold">
                                        It&apos;s not you, it&apos;s us
                                    </h1>
                                    <p className="text-md margin-top-lg">
                                        We&apos;re having a connection problem. <br /> Wait a few
                                        minutes and please try again
                                    </p>
                                </span>
                                {support}
                            </div>
                            <div hidden={defaultError} className="float-right pad-top-lg">
                                <span>
                                    <h1 className="margin-all-none font-bold">
                                        We&apos;ve run into a little glitch
                                    </h1>
                                    <p className="text-md margin-top-lg">
                                        We are looking into what happened so we can fix it.
                                        <br /> Wait a few minutes and please try again.
                                    </p>
                                </span>
                                {support}
                            </div>
                        </div>
                        <div className="text-right text-xs" hidden={traceId == null}>
                            Trace ID: {traceId}
                        </div>
                    </SBox>
                </SCol>
            </SRow>
        </div>
    );
});

ErrorPage.displayName = "ErrorPage";

export default ErrorPage;
